/**
 * The lints of one document over time, as a host shows them: the file's own diagnostics at once, beside the cross-file
 * ones of the last read, and the full list once the files it uses are read. It keeps what a host would otherwise keep
 * per document: the last cross-file diagnostics, which lint is newest, and the paths the newest one read.
 */
import { mergeDiagnostics } from './mergeDiagnostics.js';
const NOTHING_KEPT = { diagnostics: [], gltfUses: new Map() };
/**
 * `kept` moved onto the headings of `current`. A diagnostic about an `[ext_resource]` (its `nodeName` is the id) goes
 * to that id's heading in the new parse, or is dropped where the id is gone or now names another file. One about the
 * whole file stays as it was.
 */
function reanchored(kept, current) {
    const moved = [];
    for (const diagnostic of kept.diagnostics) {
        const before = kept.gltfUses.get(diagnostic.nodeName);
        if (before === undefined) {
            moved.push(diagnostic);
            continue;
        }
        const now = current.get(diagnostic.nodeName);
        if (now === undefined || now.path !== before.path)
            continue;
        moved.push({ ...diagnostic, location: now.location });
    }
    return moved;
}
export class LintSession {
    lintProject;
    /** Written when a newest lint's read lands, and cleared by a lint that reads nothing. */
    kept = NOTHING_KEPT;
    /** The newest lint, written by `lint`, so an overtaken read never lands. */
    newest = null;
    newestReads = [];
    constructor(lintProject) {
        this.lintProject = lintProject;
    }
    /**
     * The `res://` paths the newest lint's cross-file rules read, or may read: the glTF files it uses and the project
     * files the plugin probe reads. A host re-lints the document when one of them changes. Empty for a file that uses
     * nothing a cross-file rule reads.
     */
    get reads() {
        return this.newestReads;
    }
    /**
     * Lints `content`, reading the files it uses through `provider`. With no provider, only the file's own rules run.
     *
     * @param content - Raw TSCN or TRES file content
     * @param provider - Loads a `res://` path under the file's `res://` root, or null for a file with no root
     */
    lint(content, provider) {
        const lint = this.lintProject(content, provider);
        const token = Symbol('lint');
        this.newest = token;
        this.newestReads = lint.reads;
        if (lint.crossFile === null) {
            this.kept = NOTHING_KEPT;
            return { now: mergeDiagnostics(lint.local, []), later: null };
        }
        const now = mergeDiagnostics(lint.local, reanchored(this.kept, lint.gltfUses));
        const later = lint.crossFile.then((crossFile) => {
            if (this.newest !== token)
                return null;
            this.kept = { diagnostics: crossFile, gltfUses: lint.gltfUses };
            return mergeDiagnostics(lint.local, crossFile);
        });
        return { now, later };
    }
}
//# sourceMappingURL=LintSession.js.map